import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  DndContext,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
  useDroppable,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { Map as MapIcon, Plus, Check, X } from 'lucide-react';
import {
  fetchRegions,
  createRegion,
  renameRegion,
  deleteRegion,
  moveRegion,
  type RegionNode,
} from '../../api/regions';
import RegionRow from './components/RegionRow';

// ============================================
// CONSTANTS & HELPERS
// ============================================

const ROOT_COLORS = [
  '#059669', '#2563eb', '#dc2626', '#9333ea', '#ea580c',
  '#0891b2', '#ca8a04', '#be185d', '#4f46e5', '#16a34a',
];

const ROOT_DROP_ID = '__root__';

/** Flatten tree to a map of regionId → hex color inherited from root ancestor. */
function buildColorMap(regions: RegionNode[]): Map<string, string> {
  const map = new Map<string, string>();
  function traverse(node: RegionNode, color: string) {
    map.set(node.id, color);
    node.children.forEach((c) => traverse(c, color));
  }
  regions.forEach((r, i) => traverse(r, ROOT_COLORS[i % ROOT_COLORS.length]));
  return map;
}

/** Collect a region's own id + every descendant id. */
function collectDescendants(regions: RegionNode[], id: string): Set<string> {
  const out = new Set<string>();
  function findAndCollect(nodes: RegionNode[]): RegionNode | null {
    for (const n of nodes) {
      if (n.id === id) return n;
      const inner = findAndCollect(n.children);
      if (inner) return inner;
    }
    return null;
  }
  function collect(n: RegionNode) {
    out.add(n.id);
    n.children.forEach(collect);
  }
  const start = findAndCollect(regions);
  if (start) collect(start);
  return out;
}

/** Find a region's current parentId in the tree. */
function findParentId(regions: RegionNode[], id: string, parentId: string | null = null): string | null | undefined {
  for (const n of regions) {
    if (n.id === id) return parentId;
    const inner = findParentId(n.children, id, n.id);
    if (inner !== undefined) return inner;
  }
  return undefined;
}

// ============================================
// ROOT DROP ZONE
// ============================================

interface RootDropZoneProps {
  active: boolean;
}

function RootDropZone({ active }: RootDropZoneProps) {
  const { setNodeRef, isOver } = useDroppable({ id: ROOT_DROP_ID });
  if (!active) return null;
  return (
    <div
      ref={setNodeRef}
      className={[
        'mx-3 my-2 px-3 py-2 rounded-lg border-2 border-dashed text-xs text-center transition-colors',
        isOver
          ? 'border-moss-500 bg-moss-50 dark:bg-moss-900/20 text-moss-700 dark:text-moss-300'
          : 'border-stone-300 dark:border-stone-700 text-stone-500 dark:text-stone-400',
      ].join(' ')}
    >
      Drop here to make top-level
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

const RegionSubregionManager: React.FC = () => {
  const [regions, setRegions] = useState<RegionNode[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  // Inline-edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  // Add-child state (parentId being added under)
  const [addingChildOf, setAddingChildOf] = useState<string | null>(null);
  const [newChildName, setNewChildName] = useState('');

  // Add top-level region state
  const [showAddRoot, setShowAddRoot] = useState(false);
  const [newRootName, setNewRootName] = useState('');

  // Drag state
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const colorMap = useMemo(() => buildColorMap(regions), [regions]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const loadData = useCallback(async () => {
    try {
      setError(null);
      const regionData = await fetchRegions();
      setRegions(regionData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load regions');
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // ---- Rename ----
  function handleEditStart(id: string, name: string) {
    setEditingId(id);
    setEditName(name);
  }

  async function handleEditSave(id: string) {
    const name = editName.trim();
    if (!name) {
      setEditingId(null);
      return;
    }
    try {
      await renameRegion(id, name);
      setEditingId(null);
      setEditName('');
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rename region');
    }
  }

  function handleEditCancel() {
    setEditingId(null);
    setEditName('');
  }

  // ---- Add child ----
  function handleAddChildStart(parentId: string) {
    setAddingChildOf(parentId);
    setNewChildName('');
    // Auto-expand the parent so the new child is visible after save.
    setExpanded((prev) => new Set(prev).add(parentId));
  }

  async function handleAddChildSave(parentId: string) {
    const name = newChildName.trim();
    if (!name) {
      setAddingChildOf(null);
      return;
    }
    try {
      await createRegion(name, parentId);
      setAddingChildOf(null);
      setNewChildName('');
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create region');
    }
  }

  function handleAddChildCancel() {
    setAddingChildOf(null);
    setNewChildName('');
  }

  // ---- Add top-level ----
  async function handleAddRootSave() {
    const name = newRootName.trim();
    if (!name) {
      setShowAddRoot(false);
      return;
    }
    try {
      await createRegion(name);
      setShowAddRoot(false);
      setNewRootName('');
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create region');
    }
  }

  // ---- Delete ----
  async function handleDelete(id: string, name: string) {
    if (!window.confirm(`Delete region "${name}"? Any child regions will be re-parented to its parent.`)) return;
    try {
      await deleteRegion(id);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete region');
    }
  }

  // ---- Drag and drop ----
  const isValidDrop = useCallback(
    (draggedId: string, targetId: string) => {
      if (draggedId === targetId) return false;
      const blocked = collectDescendants(regions, draggedId);
      return !blocked.has(targetId);
    },
    [regions],
  );

  function handleDragStart(e: DragStartEvent) {
    setDraggingId(String(e.active.id));
  }

  async function handleDragEnd(e: DragEndEvent) {
    const draggedId = String(e.active.id);
    setDraggingId(null);
    if (!e.over) return;
    const overId = String(e.over.id);

    let nextParentId: string | null;
    if (overId === ROOT_DROP_ID) {
      nextParentId = null;
    } else {
      if (!isValidDrop(draggedId, overId)) return;
      nextParentId = overId;
    }

    // No-op if parent didn't change.
    const currentParent = findParentId(regions, draggedId);
    if (currentParent === nextParentId) return;

    try {
      await moveRegion(draggedId, nextParentId);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to move region');
    }
  }

  function countAll(nodes: RegionNode[]): number {
    return nodes.reduce((acc, n) => acc + 1 + countAll(n.children), 0);
  }

  const totalRegions = countAll(regions);

  return (
    <div className="bg-white dark:bg-[#1e2521] rounded-xl border border-stone-200 dark:border-stone-700 flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 px-5 py-4 border-b border-stone-200 dark:border-stone-700 flex-shrink-0">
        <MapIcon size={16} className="text-moss-600 dark:text-moss-400" />
        <h2 className="text-base font-semibold text-bark-900 dark:text-stone-100">Regions</h2>
        <span className="ml-3 text-xs text-stone-400">{totalRegions} regions</span>
        <button
          className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg border border-stone-300 dark:border-stone-600 text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-[#242d28] transition-colors"
          onClick={() => {
            setShowAddRoot(true);
            setNewRootName('');
          }}
        >
          <Plus size={12} />
          New top-level region
        </button>
      </div>

      {error && (
        <div className="mx-3 mt-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-400 flex-shrink-0 flex items-center justify-between gap-2">
          <span>{error}</span>
          <button
            className="p-0.5 text-red-500 hover:text-red-700"
            onClick={() => setError(null)}
            aria-label="Dismiss error"
          >
            <X size={12} />
          </button>
        </div>
      )}

      {/* Add top-level form */}
      {showAddRoot && (
        <div className="flex items-center gap-1.5 px-4 py-2 border-b border-stone-200 dark:border-stone-700">
          <input
            autoFocus
            className="flex-1 text-sm px-2 py-1 rounded border border-moss-400 dark:border-moss-600 bg-white dark:bg-[#242d28] text-stone-900 dark:text-stone-100"
            placeholder="Top-level region name…"
            value={newRootName}
            onChange={(e) => setNewRootName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleAddRootSave();
              if (e.key === 'Escape') {
                setShowAddRoot(false);
                setNewRootName('');
              }
            }}
          />
          <button
            className="p-1 text-moss-600 hover:text-moss-800 dark:text-moss-400"
            onClick={() => void handleAddRootSave()}
          >
            <Check size={14} />
          </button>
          <button
            className="p-1 text-stone-400 hover:text-stone-600"
            onClick={() => {
              setShowAddRoot(false);
              setNewRootName('');
            }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <RootDropZone active={draggingId !== null} />

        <div className="flex-1 overflow-y-auto py-2 px-1">
          {regions.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-stone-400 dark:text-stone-600">
              No regions yet.
            </div>
          ) : (
            regions.map((r) => (
              <RegionRow
                key={r.id}
                node={r}
                depth={0}
                expanded={expanded}
                onToggle={toggleExpand}
                editingId={editingId}
                editName={editName}
                onEditStart={handleEditStart}
                onEditChange={setEditName}
                onEditSave={handleEditSave}
                onEditCancel={handleEditCancel}
                addingChildOf={addingChildOf}
                newChildName={newChildName}
                onAddChildStart={handleAddChildStart}
                onNewChildNameChange={setNewChildName}
                onAddChildSave={handleAddChildSave}
                onAddChildCancel={handleAddChildCancel}
                onDelete={handleDelete}
                colorMap={colorMap}
                draggingId={draggingId}
                isValidDrop={isValidDrop}
              />
            ))
          )}
        </div>
      </DndContext>
    </div>
  );
};

export default RegionSubregionManager;
